// クラシック版のボタンを並べる部品（段階 C の2 の 5a。2026-10-25 に charat.common.js の makeMenuButton・makeFloatButton と、画面ファイル gen1.js の makeMenuButton から描き直した。見た目・動きは前と同じ）。
//   makeMenuButton: 右の列の縦に並ぶメニューのボタン（色・柄・移動など）。ON のときは横にずれて飛び出して見える。押したときの動き（横にずらす）は charat.common.moveButton（d3 の動き）
//   makeFloatButton: 浮いた丸いボタン（完成・色を自由に選ぶ・色の入れかえなど）。セーブスロット（slots.js）がこれを包んで「完成」の横にスロットのボタンを足す
// メニューのボタンの押す所のクリックは d3 で付ける（ほかの所が d3 で取り出して呼ぶ）。
// 第1世代（SCREEN gen1a・gen1b、前の gen1.js）のメニューのボタンは、押すと VALID を見ずに切りかえ、要求型（ModeReq）が無い
import { common } from './engine.js';
import { screen } from './colors.js';
import { bindData, onClick, setAttr, setStyle, setXlinkHref, svgEl } from './dom.js';
import * as fx from './fx.js';
import type { Selection } from './selection.js';

/** ボタンの定義（chibi.btnXxx）。STATUS は今の状態（0・1、選択型は 1 から）、FILE は状態ごとの絵 */
interface ButtonDef {
  ID: string;
  STATUS: number;
  STATUS_INIT?: boolean;
  FILE: string[];
  MODE?: number;
  VALID?: () => boolean;
  CLICK: ((done?: () => void) => void) | null;
}
interface MenuItem {
  BTN: ButtonDef;
  TOP: number;
  COLOR: string[];
  X?: number;
  Y?: number;
  W?: number;
  H?: number;
}
interface MenuList {
  LAYER: Selection;
  CLASS: string;
  VISIBLE?: boolean;
  SIZE: { W: number; H: number };
  STYLE?: string;
  POS?: string;
  SCALE: number;
  DATA: MenuItem[];
}
interface FloatItem {
  BTN: ButtonDef;
  POS: { X: number; Y: number };
  SIZE: { W: number; H: number };
  SCALE: number;
  COLOR: string[];
}
interface FloatList {
  LAYER: Selection;
  CLASS: string;
  VISIBLE?: boolean;
  REAR?: string;
  DATA: FloatItem[];
}
type Common = {
  makeMenuButtonModeSW: number;
  makeMenuButtonModeSel: number;
  makeMenuButtonModeReq: number;
  moveButton(id: string, pos: number, dur: number, ease: unknown): void;
};
const C = (): Common => common() as unknown as Common;
const isGen1 = (): boolean => screen() === 'gen1a' || screen() === 'gen1b';

/** 入れ物（LAYER）の要素（d3 の選択から） */
const layerNode = (layer: Selection): Element | null => (layer ? layer.node() : null);

/** メニューのボタンを aButtonList のとおりに並べる */
export function makeMenuButton(aButtonList: MenuList): void {
  const aW = aButtonList.SIZE.W, aH = aButtonList.SIZE.H;
  const gen1 = isGen1();
  // 角丸の大きさ rsize、ボタンの横の位置 pad.X、OFF・ON のときの横のずれ padpos[0]・padpos[1]（"ls" はどれも 0）
  const rsize = aButtonList.STYLE == 'r' ? aW / 9 : 0;
  const pad = { X: 0, Y: 0 };
  const padpos = [0, 0];
  if (aButtonList.POS == 'r') {
    pad.X = Number(aButtonList.LAYER.attr('width')) - aW;
    padpos[0] = aW / 3;
    padpos[1] = rsize;
  } else if (aButtonList.POS == 'l') {
    pad.X = 0;
    padpos[0] = -(aW / 3);
    padpos[1] = -rsize;
  }
  const layer = layerNode(aButtonList.LAYER);
  if (!layer) return;
  // ボタンごとの g と、状態の色の rect（位置と大きさを DATA の各項目に X・Y・W・H として書く）
  const groups = aButtonList.DATA.map((d) => {
    const g = svgEl('g', { class: aButtonList.CLASS });
    setAttr(g, 'id', d.BTN.ID);
    setAttr(g, 'transform', 'translate(' + padpos[d.BTN.STATUS] + ', 0)');
    bindData(g, d);
    layer.appendChild(g);
    const r = svgEl('rect');
    setStyle(r, 'fill', d.COLOR[d.BTN.STATUS]);
    d.X = pad.X;
    d.Y = d.TOP * aH;
    d.W = aW;
    d.H = aH;
    setAttr(r, 'x', d.X);
    setAttr(r, 'y', d.Y);
    setAttr(r, 'width', d.W);
    setAttr(r, 'height', d.H);
    setAttr(r, 'rx', rsize);
    setAttr(r, 'ry', rsize);
    bindData(r, d);
    g.appendChild(r);
    return g;
  });
  // ボタンの絵（CLICK のあるボタンだけ。今の状態の FILE）
  aButtonList.DATA.forEach((d, i) => {
    if (d.BTN.CLICK == null) return;
    const img = svgEl('image');
    setXlinkHref(img, d.BTN.FILE[d.BTN.STATUS]);
    setAttr(img, 'x', aButtonList.POS == 'ls' ? d.X! + (aW * (1 - aButtonList.SCALE)) / 2 : d.X! + aW * ((1 - aButtonList.SCALE) / 8));
    setAttr(img, 'y', d.Y! + aH * ((1 - aButtonList.SCALE) / 2));
    setAttr(img, 'width', aW * aButtonList.SCALE);
    setAttr(img, 'height', aH * aButtonList.SCALE);
    bindData(img, d);
    groups[i].appendChild(img);
  });
  // 押す所（透明な rect を重ねる。CLICK のあるボタンだけ）
  aButtonList.DATA.forEach((d, i) => {
    if (d.BTN.CLICK == null) return;
    const hit = svgEl('rect');
    setAttr(hit, 'id', d.BTN.ID + 'click');
    setAttr(hit, 'x', d.X);
    setAttr(hit, 'y', d.Y);
    setAttr(hit, 'width', d.W);
    setAttr(hit, 'height', d.H);
    setAttr(hit, 'rx', rsize);
    setAttr(hit, 'ry', rsize);
    setStyle(hit, 'fill', '#000000');
    setStyle(hit, 'opacity', 0);
    setStyle(hit, 'cursor', 'pointer');
    bindData(hit, d);
    // クリックは d3 で付ける（ほかの所が d3.select("#<ID>click").on("click")({ BTN: … }) で取り出して、ボタンの定義を渡して呼ぶ。呼ぶ所を描き直したら onClick に）
    fx.d3Click(hit, (dd?: unknown) => clickMenu(dd ? (dd as MenuItem) : d, padpos, gen1));
    groups[i].appendChild(hit);
  });
  // 隠す指定（注意: LAYER に関係なく、同じ CLASS のボタンをページ全体で隠す。前と同じ）
  if (aButtonList.VISIBLE == false) document.querySelectorAll('g.' + aButtonList.CLASS).forEach((g) => g.setAttribute('visibility', 'hidden'));
}

/** ボタンの絵を替える（jQuery の $("#ID>image").attr("href", 値)。名前空間の無い href。値が undefined なら何もしない） */
function setImageHref(id: string, value: string | undefined): void {
  if (value === undefined) return;
  document.querySelectorAll('#' + id + '>image').forEach((el) => setAttr(el, 'href', value));
}

/** メニューのボタンを押したとき（切りかえ型・選択型・要求型）。d はボタンの定義 BTN を持つもの（ほかの所から呼ぶときは { BTN } だけ） */
function clickMenu(d: Pick<MenuItem, 'BTN'>, padpos: number[], gen1: boolean): void {
  const b = d.BTN, c = C(), id = '#' + b.ID;
  if (b.MODE == undefined || b.MODE == c.makeMenuButtonModeSW) {
    // 切りかえ型: STATUS を反転して横にずらし、CLICK の終わりにもう一度位置を合わせる（第1世代は VALID を見ない）
    if (gen1 || b.VALID == undefined || b.VALID()) {
      b.STATUS = b.STATUS == 0 ? 1 : 0;
      c.moveButton(id, padpos[b.STATUS], 100, fx.ease('elastic'));
      b.CLICK!(() => {
        C().moveButton(id, padpos[b.STATUS], 100, fx.ease('elastic'));
      });
    }
  } else if (b.MODE == c.makeMenuButtonModeSel) {
    // 選択型: はじめ（STATUS_INIT が false）は最初の絵に戻して VALID に応じた位置へ。2 回目からは VALID のときだけ CLICK して、STATUS の絵に替えて一度引っこめてから飛び出させる
    if (b.STATUS_INIT == false) {
      setImageHref(b.ID, b.FILE[0]);
      c.moveButton(id, padpos[b.VALID!() ? 1 : 0], 100, fx.ease('elastic'));
      b.CLICK!();
    } else if (b.VALID!()) {
      b.CLICK!();
      setImageHref(b.ID, b.FILE[b.STATUS - 1]);
      c.moveButton(id, padpos[0], 0, fx.ease('linear'));
      c.moveButton(id, padpos[1], 100, fx.ease('backOut'));
    }
  } else if (!gen1 && b.MODE == c.makeMenuButtonModeReq) {
    // 要求型: CLICK の終わりに、VALID なら STATUS の絵に替えて飛び出させ、そうでなければ最初の絵で引っこめる
    b.CLICK!(() => {
      setImageHref(b.ID, b.FILE[!b.VALID!() ? 0 : b.STATUS - 1]);
      if (b.VALID!()) {
        C().moveButton(id, padpos[0], 0, fx.ease('linear'));
        C().moveButton(id, padpos[1], 100, fx.ease('backOut'));
      } else {
        C().moveButton(id, padpos[0], 100, fx.ease('elastic'));
      }
    });
  }
}

/** 浮いた丸いボタンを aButtonList のとおりに置く（REAR が "circle" なら下に丸を敷き、押す所も丸） */
export function makeFloatButton(aButtonList: FloatList): void {
  const layer = layerNode(aButtonList.LAYER);
  if (!layer) return;
  const circle = aButtonList.REAR == 'circle';
  const groups = aButtonList.DATA.map((d) => {
    const g = svgEl('g', { class: aButtonList.CLASS });
    setAttr(g, 'id', d.BTN.ID);
    setStyle(g, 'outline', 'none');
    bindData(g, d);
    layer.appendChild(g);
    return g;
  });
  const rear = (d: FloatItem): SVGCircleElement => {
    const c = svgEl('circle');
    setStyle(c, 'fill', d.COLOR[d.BTN.STATUS]);
    setAttr(c, 'cx', d.POS.X + d.SIZE.W / 2);
    setAttr(c, 'cy', d.POS.Y + d.SIZE.H / 2);
    setAttr(c, 'r', d.SIZE.W * 0.5 * d.SCALE);
    bindData(c, d);
    return c;
  };
  const press = (ev: Event, d: FloatItem): void => {
    ev.stopPropagation();
    d.BTN.STATUS = d.BTN.STATUS == 0 ? 1 : 0;
    d.BTN.CLICK!(() => {});
  };
  // 下の丸
  if (circle) aButtonList.DATA.forEach((d, i) => groups[i].appendChild(rear(d)));
  // ボタンの絵（丸の無いボタンは絵を押す）
  aButtonList.DATA.forEach((d, i) => {
    const img = svgEl('image');
    setXlinkHref(img, d.BTN.FILE[d.BTN.STATUS]);
    setAttr(img, 'x', d.POS.X);
    setAttr(img, 'y', d.POS.Y);
    setAttr(img, 'width', d.SIZE.W);
    setAttr(img, 'height', d.SIZE.H);
    setStyle(img, 'cursor', 'pointer');
    bindData(img, d);
    if (!circle) onClick(img, (ev) => press(ev, d));
    groups[i].appendChild(img);
  });
  // 押す所（丸のボタンは絵の上に透明な丸）
  if (circle) {
    aButtonList.DATA.forEach((d, i) => {
      const hit = rear(d);
      setStyle(hit, 'fill', '#000000');
      setStyle(hit, 'opacity', 0);
      setStyle(hit, 'cursor', 'pointer');
      onClick(hit, (ev) => press(ev, d));
      groups[i].appendChild(hit);
    });
  }
}
